import React, { useEffect, useState, useCallback } from "react";
import { getApiBaseUrl, authenticatedRequest } from './Utils.js'
import { useAuth } from "./AuthContext";
import BillingNav from "./BillingNav";

const MONTHS = [
  { key: "1", label: "Jan" },
  { key: "2", label: "Feb" },
  { key: "3", label: "Mar" },
  { key: "4", label: "Apr" },
  { key: "5", label: "May" },
  { key: "6", label: "Jun" },
  { key: "7", label: "Jul" },
  { key: "8", label: "Aug" },
  { key: "9", label: "Sep" },
  { key: "10", label: "Oct" },
  { key: "11", label: "Nov" },
  { key: "12", label: "Dec" }
];

function formatDollar(value) {
  const num = typeof value === "number" ? value : parseFloat(value) || 0;
  if (num < 0) {
    return "($" + Math.abs(num).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ")";
  }
  return "$" + num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatReportDate() {
  const now = new Date();
  return now.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

const BillingSummaryByYear = () => {
  const authUser = useAuth();
  const [apiURL, setApiUrl] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastProcessed, setLastProcessed] = useState(null);

  useEffect(() => {
    if (authUser && authUser.access_token) {
      getApiBaseUrl(setApiUrl, authUser.access_token);
    }
  }, [authUser]);

  useEffect(() => {
    if (!apiURL || !authUser) return;
    authenticatedRequest(
      apiURL + "/api/reports/billing/years",
      authUser.access_token,
      (data) => setYears(data || []),
      () => setYears([])
    );

    authenticatedRequest(
      apiURL + "/api/reports/billing/last-processed",
      authUser.access_token,
      (data) => setLastProcessed(data.lastProcessedDateTime || null),
      () => setLastProcessed(null)
    );
  }, [apiURL, authUser]);

  const fetchReport = useCallback(() => {
    if (!apiURL || !authUser || !selectedYear) return;
    setLoading(true);
    setError(null);
    setReportData(null);

    const url = apiURL + "/api/reports/billing/summary-by-year?year="
      + encodeURIComponent(selectedYear);

    authenticatedRequest(
      url,
      authUser.access_token,
      (data) => {
        setReportData(data);
        setLoading(false);
      },
      () => {
        setError("Failed to load report data.");
        setLoading(false);
      }
    );
  }, [apiURL, authUser, selectedYear]);

  useEffect(() => {
    if (selectedYear) {
      fetchReport();
    } else {
      setReportData(null);
    }
  }, [selectedYear, fetchReport]);

  if (!authUser) {
    sessionStorage.setItem("app:redirect", location.href);
    window.location.replace('/');
    return null;
  }

  function renderTable() {
    if (!reportData || !reportData.practiceAreas || reportData.practiceAreas.length === 0) {
      return <p className="billing-no-data">No data available for the selected year.</p>;
    }

    return (
      <div className="billing-table-wrapper">
        <table className="billing-table billing-summary-table">
          <thead>
            <tr>
              <th>Practice Area</th>
              {MONTHS.map(m => <th key={m.key}>{m.label}</th>)}
              <th>Totals for Year</th>
            </tr>
          </thead>
          <tbody>
            {reportData.practiceAreas.map((pa, paIdx) => {
              const monthly = pa.monthly || {};
              const altClass = paIdx % 2 === 1 ? " billing-alt-group" : "";
              return (
                <React.Fragment key={pa.practiceArea}>
                  <tr className={"billing-row-pa-header" + altClass}>
                    <td colSpan={MONTHS.length + 2} className="billing-cell-practice-area">{pa.practiceArea}</td>
                  </tr>
                  <tr className={"billing-row-budget" + altClass}>
                    <td className="billing-cell-label billing-indent">Budget</td>
                    {MONTHS.map(m => {
                      const val = monthly[m.key] ? monthly[m.key].budget : 0;
                      return <td key={m.key} className="billing-cell-amount">{formatDollar(val)}</td>;
                    })}
                    <td className="billing-cell-amount billing-cell-year-total">{formatDollar(pa.yearBudget)}</td>
                  </tr>
                  <tr className={"billing-row-spend" + altClass}>
                    <td className="billing-cell-label billing-indent">Spend</td>
                    {MONTHS.map(m => {
                      const val = monthly[m.key] ? monthly[m.key].spend : 0;
                      return <td key={m.key} className="billing-cell-amount">{formatDollar(val)}</td>;
                    })}
                    <td className="billing-cell-amount billing-cell-year-total">{formatDollar(pa.yearSpend)}</td>
                  </tr>
                  <tr className={"billing-row-variance" + altClass}>
                    <td className="billing-cell-label billing-label-variance billing-indent">Variance</td>
                    {MONTHS.map(m => {
                      const b = monthly[m.key] ? monthly[m.key].budget : 0;
                      const s = monthly[m.key] ? monthly[m.key].spend : 0;
                      const variance = b - s;
                      return (
                        <td key={m.key} className={"billing-cell-amount" + (variance < 0 ? " billing-negative" : "")}>
                          {formatDollar(variance)}
                        </td>
                      );
                    })}
                    {(() => {
                      const yearVariance = (pa.yearBudget || 0) - (pa.yearSpend || 0);
                      return (
                        <td className={"billing-cell-amount billing-cell-year-total" + (yearVariance < 0 ? " billing-negative" : "")}>
                          {formatDollar(yearVariance)}
                        </td>
                      );
                    })()}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      <div className="billing-report-page">
        <BillingNav active="summary-by-year" />
        <div className="billing-filters no-print">
          <div className="billing-filter-group">
            <label htmlFor="billing-year">Select Year</label>
            <select
              id="billing-year"
              className="form-control billing-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {years.length === 0 && <option value={selectedYear}>{selectedYear}</option>}
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="billing-report-header">
          <div className="billing-header-left">
            <img src="/images/bnsf-logo.png" alt="BNSF Railway" className="billing-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <div className="billing-header-center">
            <div className="billing-title">Legal Billing Summary by Year</div>
            <div className="billing-practice-area-name">{selectedYear}</div>
          </div>
          <div className="billing-header-right">
            <div className="billing-date">{formatReportDate()}</div>
            {lastProcessed && <div className="billing-last-processed">Data as of: {lastProcessed}</div>}
          </div>
        </div>

        {loading && <div className="billing-loading"><i className="fa fa-spinner fa-spin fa-2x"></i> Loading...</div>}
        {error && <div className="billing-error">{error}</div>}
        {!loading && !error && reportData && renderTable()}
      </div>
    </div>
  );
}

export default BillingSummaryByYear;
